import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useWorkspace } from "../contexts/WorkspaceContext";
import Topbar from "../components/Topbar";
import { Layers, Sparkles, ArrowRight } from "lucide-react";

const BRAND_HERO = {
  luxury: { from: "#7C2D12", to: "#FCA5A5", img: "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?auto=format&fit=crop&w=600&q=70" },
  elegance: { from: "#1E3A8A", to: "#93C5FD", img: "https://images.unsplash.com/photo-1564501049412-61c2a3083791?auto=format&fit=crop&w=600&q=70" },
  resort: { from: "#9F1239", to: "#FDA4AF", img: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=600&q=70" },
  smart: { from: "#DC2626", to: "#FCA5A5", img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=600&q=70" },
};

export default function Templates() {
  const { currentWorkspaceId } = useWorkspace();
  const [templates, setTemplates] = useState([]);
  const [activating, setActivating] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", start_date: "" });
  const nav = useNavigate();

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data));
  }, []);

  const create = async () => {
    if (!form.name) return;
    const r = await api.post("/projects/from-template", {
      template_brand: activating, name: form.name, code: form.code,
      start_date: form.start_date || null, due_date: null,
    }, { params: { workspace_id: currentWorkspaceId } });
    nav(`/projects/${r.data.id}`);
  };

  return (
    <div data-testid="templates-page">
      <Topbar
        title="Pre-Opening Templates"
        subtitle="Brand-standard pre-opening playbooks loaded with tasks, sequencing, and dependencies"
        breadcrumb="Library"
      />
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="templates-grid">
        {templates.map((t) => {
          const hero = BRAND_HERO[t.brand];
          return (
            <div key={t.brand} data-testid={`template-${t.brand}`} className="bg-[#141417] border border-zinc-800 rounded-lg overflow-hidden hover:border-zinc-600 transition-colors">
              <div className="h-32 relative" style={{
                backgroundImage: `linear-gradient(135deg, ${hero?.from}cc, ${hero?.to}33), url(${hero?.img})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}>
                <div className="absolute top-3 left-3 text-[10px] uppercase font-mono tracking-widest text-white bg-black/40 backdrop-blur-sm px-2 py-1 rounded">
                  {t.brand}
                </div>
                <div className="absolute bottom-3 left-3 text-white">
                  <div className="text-base font-bold">{t.label}</div>
                </div>
              </div>
              <div className="p-4">
                <p className="text-xs text-zinc-400 leading-relaxed mb-3">{t.description}</p>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {t.tasklists.map((tl) => (
                    <span key={tl.name} className="text-[10px] uppercase font-mono tracking-widest text-zinc-300 bg-[#0B0B0E] border border-zinc-800 px-1.5 py-0.5 rounded">
                      {tl.name} · {tl.task_count}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase font-mono tracking-widest text-zinc-500">
                    <Layers className="w-3 h-3" />
                    {t.total_tasks} pre-loaded tasks
                  </div>
                  <button
                    onClick={() => { setActivating(t.brand); setForm({ name: t.label.split("—")[0].trim() + " — " + new Date().getFullYear(), code: "", start_date: new Date().toISOString().slice(0, 10) }); }}
                    data-testid={`use-template-${t.brand}`}
                    className="flex items-center gap-1 bg-[#D4A968] hover:bg-[#E8C285] text-black px-3 py-1.5 rounded text-xs font-semibold"
                  >
                    Use template <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {activating && (
        <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4" onClick={() => setActivating(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="template-modal" className="bg-[#0B0B0E] border border-zinc-800 rounded-lg p-5 w-full max-w-md">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-[#D4A968]" />
              <h3 className="text-lg font-bold text-white">Bootstrap from {activating}</h3>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] uppercase font-mono tracking-widest text-zinc-500 mb-1">Project name</label>
                <input
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="template-form-name"
                  className="w-full bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded px-2.5 py-2 text-sm text-white outline-none"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase font-mono tracking-widest text-zinc-500 mb-1">Code</label>
                <input
                  value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="H6938"
                  data-testid="template-form-code"
                  className="w-full bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded px-2.5 py-2 text-sm text-white outline-none font-mono"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase font-mono tracking-widest text-zinc-500 mb-1">Start date</label>
                <input
                  type="date" value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="template-form-start"
                  className="w-full bg-[#141417] border border-zinc-800 focus:border-[#D4A968] rounded px-2.5 py-2 text-sm text-white outline-none font-mono"
                />
              </div>
              <button onClick={create} data-testid="template-create-btn" className="w-full bg-[#D4A968] hover:bg-[#E8C285] text-black font-semibold py-2.5 rounded text-sm">
                Create project
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
